import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Checkbox } from "@admin-ui/components/primitives/base/checkbox/checkbox";
import { router } from "@inertiajs/react";
import {
  adminProgramExtractionApplicationPath,
  adminProgramExtractionPath,
  adminProgramRemotePhotoPath,
} from "@travily/routes";
import type ProgramExtraction from "@travily/types/models/AdminProgramsExtraction";
import { useMemo, useState } from "react";
import { humanize } from "@/lib/humanize";

interface ProgramExtractionPanelProps {
  programId: number;
  extraction: ProgramExtraction;
}

const SUGGESTABLE_FIELDS = [
  "tagline",
  "description",
  "format",
  "min_age",
  "max_age",
  "drop_off_available",
  "community_program",
  "instruction_languages",
  "influences",
] as const;

type SuggestableField = (typeof SUGGESTABLE_FIELDS)[number];

const FIELD_LABELS: Record<SuggestableField, string> = {
  tagline: "Tagline",
  description: "Description",
  format: "Format",
  min_age: "Min age",
  max_age: "Max age",
  drop_off_available: "Drop-off available",
  community_program: "Community program",
  instruction_languages: "Instruction languages",
  influences: "Influences",
};

export function ProgramExtractionPanel({ programId, extraction }: ProgramExtractionPanelProps) {
  const status = extraction.status;

  if (status === "pending" || status === "fetching" || status === "extracting") {
    return <RunningPanel status={status} />;
  }

  if (status === "failed") {
    return <FailedPanel programId={programId} reason={extraction.failure_reason} sources={extraction.sources} />;
  }

  return <CompletedPanel programId={programId} extraction={extraction} />;
}

type StepState = "done" | "active" | "pending";

const STEP_GLYPH: Record<StepState, string> = { done: "✓", active: "•", pending: "·" };
const STEP_GLYPH_CLASS: Record<StepState, string> = {
  done: "text-utility-green-600",
  active: "text-utility-brand-600",
  pending: "text-tertiary",
};

const RUNNING_STEPS: Array<{ key: "pending" | "fetching" | "extracting"; label: string }> = [
  { key: "pending", label: "Queued" },
  { key: "fetching", label: "Fetching sources" },
  { key: "extracting", label: "Extracting details" },
];

function RunningPanel({ status }: { status: "pending" | "fetching" | "extracting" }) {
  const currentIdx = RUNNING_STEPS.findIndex((s) => s.key === status);

  return (
    <section className="rounded-xl bg-secondary p-4 ring-1 ring-secondary ring-inset md:p-5">
      <h2 className="font-semibold text-md">AI extraction in progress</h2>
      <p className="mt-1 text-secondary text-sm">
        Hang tight — Claude is reading the sources and pulling structured details. This usually takes 10–30 seconds.
      </p>
      <ol className="mt-4 flex flex-col gap-2">
        {RUNNING_STEPS.map((step, idx) => {
          const state: StepState = idx < currentIdx ? "done" : idx === currentIdx ? "active" : "pending";
          return (
            <li key={step.key} className="flex items-center gap-2 text-sm">
              <span className={STEP_GLYPH_CLASS[state]}>{STEP_GLYPH[state]}</span>
              <span className={state === "pending" ? "text-tertiary" : "text-primary"}>{step.label}</span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function FailedPanel({
  programId,
  reason,
  sources,
}: {
  programId: number;
  reason: string | null;
  sources: ProgramExtraction["sources"];
}) {
  const onRetry = () => {
    router.post(adminProgramExtractionPath(programId), { sources }, { preserveScroll: true });
  };

  return (
    <section className="rounded-xl bg-utility-red-50 p-4 ring-1 ring-utility-red-200 ring-inset md:p-5">
      <h2 className="font-semibold text-md text-utility-red-700">AI extraction failed</h2>
      <p className="mt-1 text-sm text-utility-red-600">{reason ?? "An unknown error occurred."}</p>
      <div className="mt-3">
        <Button color="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </section>
  );
}

function CompletedPanel({ programId, extraction }: { programId: number; extraction: ProgramExtraction }) {
  const suggestions = extraction.suggested_attributes;
  const photos = extraction.suggested_photo_urls ?? [];
  const touched = extraction.touched_fields ?? [];

  const availableFields = useMemo(() => SUGGESTABLE_FIELDS.filter((f) => hasSuggestion(suggestions, f)), [suggestions]);

  const [acceptedFields, setAcceptedFields] = useState<Set<string>>(
    () => new Set(availableFields.filter((f) => !touched.includes(f))),
  );
  const [overwriteTouched, setOverwriteTouched] = useState<Set<string>>(new Set());

  const onApply = () => {
    router.post(
      adminProgramExtractionApplicationPath(programId),
      {
        accepted_fields: Array.from(acceptedFields),
        overwrite_touched: Array.from(overwriteTouched),
      },
      { preserveScroll: true },
    );
  };

  const onImportPhotos = (urls: string[], { onStart, onFinish }: { onStart: () => void; onFinish: () => void }) => {
    router.post(adminProgramRemotePhotoPath(programId), { urls }, { preserveScroll: true, onStart, onFinish });
  };

  return (
    <section className="flex flex-col gap-4 rounded-xl bg-secondary p-4 ring-1 ring-secondary ring-inset md:p-5">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-md">AI suggestions</h2>
          <p className="mt-1 text-secondary text-sm">
            Review and apply what looks right. Touched fields stay until you opt to overwrite them.
          </p>
        </div>
        <Button color="primary" size="sm" onClick={onApply} isDisabled={acceptedFields.size === 0}>
          Apply suggestions
        </Button>
      </header>

      <ul className="flex flex-col gap-2">
        {availableFields.map((field) => {
          const isTouched = touched.includes(field);
          return (
            <li key={field} className="flex items-start gap-2">
              <Checkbox
                isSelected={acceptedFields.has(field)}
                onChange={(checked) => {
                  setAcceptedFields((prev) => toggle(prev, field, checked));
                }}
              />
              <div className="flex flex-1 flex-col gap-1">
                <div className="flex items-center gap-2 font-medium text-sm">
                  {FIELD_LABELS[field]}
                  {isTouched && <span className="text-warning text-xs">(you edited this)</span>}
                </div>
                <pre className="whitespace-pre-wrap text-secondary text-xs">{previewOf(suggestions, field)}</pre>
                {isTouched && (
                  <div className="flex items-center gap-1 text-xs">
                    <Checkbox
                      isSelected={overwriteTouched.has(field)}
                      onChange={(checked) => {
                        setOverwriteTouched((prev) => toggle(prev, field, checked));
                      }}
                    >
                      Overwrite my edit
                    </Checkbox>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {photos.length > 0 && <PhotoGallery photos={photos} onImport={onImportPhotos} />}
    </section>
  );
}

const TILE_RING = {
  selected: "ring-utility-brand-600 ring-offset-2",
  unselected: "ring-transparent hover:ring-border-secondary",
} as const;

const TILE_BADGE = {
  selected: "bg-utility-brand-600 text-white",
  unselected: "bg-white/80 text-transparent group-hover:text-tertiary",
} as const;

function PhotoGallery({
  photos,
  onImport,
}: {
  photos: NonNullable<ProgramExtraction["suggested_photo_urls"]>;
  onImport: (urls: string[], opts: { onStart: () => void; onFinish: () => void }) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const visiblePhotos = useMemo(() => photos.filter((p) => !failedUrls.has(p.url)), [photos, failedUrls]);
  const allSelected = visiblePhotos.length > 0 && visiblePhotos.every((p) => selected.has(p.url));

  const handleToggle = (url: string) => setSelected((prev) => toggle(prev, url, !prev.has(url)));
  const handleSelectAll = () => setSelected(allSelected ? new Set() : new Set(visiblePhotos.map((p) => p.url)));
  const handleImageError = (url: string) => {
    setFailedUrls((prev) => new Set(prev).add(url));
    setSelected((prev) => toggle(prev, url, false));
  };

  return (
    <div className="border-secondary border-t pt-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-sm">Suggested photos</h3>
          <p className="mt-0.5 text-tertiary text-xs">
            {visiblePhotos.length} found · {selected.size} selected
            {failedUrls.size > 0 && ` · ${failedUrls.size} failed to load`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button color="tertiary" size="sm" onClick={handleSelectAll} isDisabled={visiblePhotos.length === 0}>
            {allSelected ? "Clear all" : "Select all"}
          </Button>
          <Button
            color="primary"
            size="sm"
            onClick={() =>
              onImport(Array.from(selected), {
                onStart: () => setIsSubmitting(true),
                onFinish: () => setIsSubmitting(false),
              })
            }
            isDisabled={selected.size === 0 || isSubmitting}
          >
            {selected.size === 0 ? "Import" : `Import ${selected.size}`}
          </Button>
        </div>
      </header>
      <ul className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {visiblePhotos.map((photo) => {
          const isSelected = selected.has(photo.url);
          const ringClass = isSelected ? TILE_RING.selected : TILE_RING.unselected;
          const badgeClass = isSelected ? TILE_BADGE.selected : TILE_BADGE.unselected;
          return (
            <li key={photo.url}>
              <button
                type="button"
                onClick={() => handleToggle(photo.url)}
                aria-pressed={isSelected}
                className={`group relative block w-full overflow-hidden rounded-lg ring-2 transition ${ringClass}`}
              >
                <img
                  src={photo.url}
                  alt={photo.alt ?? "Suggested program photo"}
                  className={`aspect-square w-full object-cover transition ${isSelected ? "" : "group-hover:opacity-90"}`}
                  loading="lazy"
                  decoding="async"
                  onError={() => handleImageError(photo.url)}
                />
                <span
                  className={`absolute top-2 right-2 flex size-6 items-center justify-center rounded-full font-bold text-xs transition ${badgeClass}`}
                  aria-hidden
                >
                  {isSelected ? "✓" : "+"}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function hasSuggestion(suggestions: ProgramExtraction["suggested_attributes"], field: SuggestableField): boolean {
  if (field === "description") return Boolean(suggestions.description_html);
  const value = suggestions[field as keyof typeof suggestions];
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
}

function previewOf(suggestions: ProgramExtraction["suggested_attributes"], field: SuggestableField): string {
  if (field === "description") return suggestions.description_html ?? "";
  const value = suggestions[field as keyof typeof suggestions];
  if (Array.isArray(value)) return value.join(", ");
  if (value === null || value === undefined) return "";
  return field === "format" ? humanize(String(value)) : String(value);
}

function toggle(prev: Set<string>, key: string, on: boolean): Set<string> {
  const next = new Set(prev);
  if (on) next.add(key);
  else next.delete(key);
  return next;
}
